import { useState, useEffect } from "react";
import { Modal } from "../primitives/Modal";
import { requestPasswordReset } from "../../api/auth";
import { CheckCircle } from "lucide-react";

type ForgotPasswordModalProps = {
  open: boolean;
  onClose: () => void;
};

export function ForgotPasswordModal({ open, onClose }: ForgotPasswordModalProps) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // Réinitialiser l'état quand le modal est fermé
  useEffect(() => {
    if (!open) {
      setEmail("");
      setErrorMessage("");
      setShowSuccess(false);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    try {
      await requestPasswordReset(email.trim());
      setShowSuccess(true);
    } catch (error) {
      setErrorMessage((error as Error).message || "Échec de la demande de réinitialisation");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal de succès - simple avec message et bouton OK
  if (showSuccess) {
    return (
      <Modal
        title="Lien de réinitialisation envoyé"
        open={open}
        onClose={onClose}
        showDefaultButtons={false}
        showCloseButton={true}
        size="md"
      >
        <div className="flex flex-col items-center text-center py-6 px-2">
          {/* Icône de succès */}
          <div className="mb-6">
            <div className="rounded-full bg-success/10 p-4 inline-flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-success" strokeWidth={2} />
            </div>
          </div>
          
          {/* Messages */}
          <div className="space-y-3 mb-8 max-w-sm">
            <p className="text-base font-medium text-text leading-relaxed">
              Si un compte existe pour cet email, un courriel de réinitialisation a été envoyé.
            </p>
            <p className="text-sm text-muted leading-relaxed">
              Vérifiez votre boîte de réception et suivez les instructions pour réinitialiser votre mot de passe.
            </p>
          </div>
          
          {/* Bouton OK */}
          <div className="w-full pt-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="w-full px-6 py-2.5 text-sm font-semibold rounded-md bg-accent text-text-inverted hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-accent/20"
            >
              OK
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Modal initial - formulaire avec email
  return (
    <Modal
      title="Mot de passe oublié"
      open={open}
      onClose={onClose}
      showDefaultButtons={false}
      showCloseButton={true}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text mb-2">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom.nom@entreprise.com"
            required
            autoComplete="email"
            className="w-full h-10 px-3 rounded-md border border-border bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            disabled={isSubmitting}
          />
        </div>

        {errorMessage && (
          <div className="rounded-md border border-danger bg-danger/10 p-3">
            <div className="text-sm text-danger">{errorMessage}</div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text transition-colors"
            disabled={isSubmitting}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium rounded-md bg-accent text-text-inverted hover:opacity-90 disabled:opacity-50 transition-opacity"
            disabled={isSubmitting || !email.trim()}
          >
            {isSubmitting ? "Envoi..." : "Envoyer le lien"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
